import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  Badge,
  Button as ShellButton,
  ButtonGroup,
  Icons,
  Option,
  RangeDatePicker,
  RadioButton,
  Select,
  Sizes,
  TextInput,
  Table,
  Tooltip,
} from '@sede-enterprise/shell-ds-react-framework';
import type { RangePickerValueType } from '@sede-enterprise/shell-ds-react-framework';
import {
  TBody,
  TH as Th,
  THead,
  TR as Tr,
  TD as Td,
} from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Table/components/TableElements/TableElements';
import Loader from 'src/UI/Loader';
import Button from 'src/UI/Button';
import { ALL, FLAT_DEALS_BATCH_SIZE, UNIT_OPTIONS } from './constants';
import { get, post, GenesisApi } from 'src/services/GenesisApi';
import {
  GET_FLAT_DEALS_TRADES,
  GET_FLAT_DEAL_PROFILES,
  SET_FLATDEAL_IGNORE,
  SET_FLATDEAL_REIMPORT,
} from 'src/constants/EUMarkets';
import { IFlatDeal, IFlatDealsRequest, IProfileData, IProfileDataModalState } from './types';
import { FlatDealsContainer, ColFilterGlobalStyle } from './style';
import { getCurrentGasDay, handleError } from 'src/utils/common/utils';
import { CET_TIME_ZONE, NUMBERS, COMMON_MESSAGES } from 'src/constants/Common';
import ProfileDataModal from './ProfileDataModal';

type DealView = 'flat' | 'flex';

// Conversion factor: 1 Therm ≈ 29.3071 kWh
const KWH_PER_THERM = 29.3071;

dayjs.extend(utc);
dayjs.extend(timezone);

const convertFromKWh = (valueKWh: number, toUnit: string): string => {
  switch (toUnit) {
    case 'KWh':
      return Math.round(valueKWh).toLocaleString('en-GB');
    case 'MWh':
      return (valueKWh / 1_000).toLocaleString('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      });
    case 'GWh':
      return (valueKWh / 1_000_000).toLocaleString('en-GB', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      });
    case 'Therms':
      return Math.round(valueKWh / KWH_PER_THERM).toLocaleString('en-GB');
    default:
      return Math.round(valueKWh).toLocaleString('en-GB');
  }
};

const formatDateDisplay = (iso: string): string => {
  const d = dayjs.utc(iso).tz(CET_TIME_ZONE);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : iso;
};

const {
  ArrowCw,
  ArrowDownSquare,
  ArrowUpSquare,
  ChevronUpDown,
  Filter,
  Cross,
  TrashAltSolid,
  CheckCircleSolid,
  AlertCircleSolid,
  MinusCircleSolid,
  CrossCircleSolid,
  EyeSolid,
} = Icons;

const DATE_YYYY_MM_DD = 'YYYY-MM-DD';
const INLINE_BLOCK = 'inline-block';
const INLINE_FLEX = 'inline-flex';
const IMPORT_STATUS_IGNORED = 'IGNORED';

type SortDir = 'asc' | 'desc' | null;
type FilterColumn = 'dealId' | 'counterparty' | 'deliveryPoint' | 'shellEntity';

interface SortIconProps {
  col: keyof IFlatDeal;
  sortColumn: keyof IFlatDeal | null;
  sortDir: SortDir;
}

const SortIcon = ({ col, sortColumn, sortDir }: SortIconProps) => {
  if (sortColumn !== col || !sortDir) {
    return <ChevronUpDown className='sort-icon' />;
  }
  return sortDir === 'asc' ? (
    <ArrowUpSquare className='sort-icon' />
  ) : (
    <ArrowDownSquare className='sort-icon' />
  );
};

const fetchFlatDeals = (payload: IFlatDealsRequest): Promise<IFlatDeal[]> =>
  post(GET_FLAT_DEALS_TRADES, payload as unknown as Record<string, unknown>).then(
    (r) => r.data ?? [],
  );

const getImportStatusIcon = (row: IFlatDeal): React.ReactNode => {
  if (row.dealStatus?.toUpperCase() === 'INACTIVE') {
    return (
      <AlertCircleSolid
        width={18}
        height={18}
        style={{ color: '#f9a825', display: INLINE_BLOCK }}
      />
    );
  }
  if (row.importStatus === 'SUCCESS') {
    return (
      <CheckCircleSolid
        width={18}
        height={18}
        style={{ color: '#22a06b', display: INLINE_BLOCK }}
      />
    );
  }
  return (
    <CrossCircleSolid width={18} height={18} style={{ color: '#e3342f', display: INLINE_BLOCK }} />
  );
};

interface ImportStatusCellProps {
  row: IFlatDeal;
  actionLoading: { [key: number]: boolean };
  onIgnore: (row: IFlatDeal) => void;
  onReimport: (row: IFlatDeal) => void;
}

const ImportStatusCell = ({ row, actionLoading, onIgnore, onReimport }: ImportStatusCellProps) => {
  const isError = row.importStatus === 'ERROR';
  const isIgnored = row.importStatus === IMPORT_STATUS_IGNORED;

  const renderErrorIcon = () => (
    <Tooltip
      triggerAction='hover'
      overlay={
        <div
          className='import-message-tooltip'
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}
        >
          <div>{row.importMessage?.trim() || 'Import failed'}</div>
          <button
            onClick={() => onIgnore(row)}
            disabled={actionLoading[row.id]}
            className='ignore-deal-btn'
            data-testid={`flat-deals-ignore-btn-${row.id}`}
            title='Ignore this failed import'
            style={{
              marginTop: 8,
              background: 'rgb(255, 198, 0)',
              border: '1px solid rgb(255, 198, 0)',
              borderRadius: 4,
              color: '#202020',
              padding: '2px 10px',
              cursor: actionLoading[row.id] ? 'not-allowed' : 'pointer',
            }}
          >
            Ignore
          </button>
        </div>
      }
      trigger={
        <span style={{ display: INLINE_FLEX }} data-testid={`flat-deals-error-trigger-${row.id}`}>
          <CrossCircleSolid
            width={18}
            height={18}
            style={{ color: '#e3342f', display: INLINE_BLOCK }}
          />
        </span>
      }
    />
  );

  const renderIgnoredIcon = () => (
    <Tooltip
      triggerAction='hover'
      overlay={
        <div className='import-message-tooltip'>This deal was ignored. Click to reimport it.</div>
      }
      trigger={
        <span style={{ display: INLINE_FLEX, alignItems: 'center', gap: 8 }}>
          <MinusCircleSolid
            width={18}
            height={18}
            style={{ color: '#6b7280', display: INLINE_BLOCK }}
            data-testid={`flat-deals-ignored-icon-${row.id}`}
          />
          <button
            onClick={() => onReimport(row)}
            disabled={actionLoading[row.id]}
            style={{
              background: 'none',
              border: 'none',
              cursor: actionLoading[row.id] ? 'not-allowed' : 'pointer',
              padding: 0,
              display: INLINE_FLEX,
              opacity: actionLoading[row.id] ? 0.6 : 1,
            }}
            data-testid={`flat-deals-reimport-btn-${row.id}`}
            title='Reimport this ignored deal'
          >
            <ArrowCw width={18} height={18} style={{ color: '#f9a825', display: INLINE_BLOCK }} />
          </button>
        </span>
      }
    />
  );

  if (isError) {
    return renderErrorIcon();
  }

  if (isIgnored) {
    return renderIgnoredIcon();
  }

  if (row.importStatus === 'SUCCESS') {
    return (
      <Tooltip
        triggerAction='hover'
        overlay={<div className='import-message-tooltip'>Import successful</div>}
        trigger={
          <span style={{ display: INLINE_FLEX }}>
            <CheckCircleSolid
              width={18}
              height={18}
              style={{ color: '#22a06b', display: INLINE_BLOCK }}
            />
          </span>
        }
      />
    );
  }

  return null;
};

interface DealStatusCellProps {
  row: IFlatDeal;
}

const DealStatusCell = ({ row }: DealStatusCellProps) => <span>{row.dealStatus || 'N/A'}</span>;

const FlatDeals = ({
  activeView,
  onViewChange,
}: {
  activeView: DealView;
  onViewChange: (view: DealView) => void;
}) => {
  const [visibleRows, setVisibleRows] = useState<IFlatDeal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const hasMoreRef = useRef(true);

  // Profile data modal state
  const [profileModalState, setProfileModalState] = useState<IProfileDataModalState>({
    isOpen: false,
    profileData: [],
    isLoading: false,
  });

  // Ignore/Reimport action state
  const [actionLoading, setActionLoading] = useState<{ [key: number]: boolean }>({});

  // Date filter state — matches Trading screen pattern
  const [isViewTradesCreatedOn, setIsViewTradesCreatedOn] = useState(true);
  const [periodFrom, setPeriodFrom] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );
  const [periodTo, setPeriodTo] = useState<string>(() =>
    getCurrentGasDay().format(DATE_YYYY_MM_DD),
  );

  // Filter state
  const [searchKey, setSearchKey] = useState('');
  const [filterType, setFilterType] = useState<string>(ALL);
  const [filterUnit, setFilterUnit] = useState<string>('KWh');
  const [filterShellEntity, setFilterShellEntity] = useState<string>('');
  const [filterCounterparty, setFilterCounterparty] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [draftDealId, setDraftDealId] = useState('');
  const [draftShellEntity, setDraftShellEntity] = useState('');
  const [draftCounterparty, setDraftCounterparty] = useState('');
  const [draftLocation, setDraftLocation] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [debouncedShellEntity, setDebouncedShellEntity] = useState('');
  const [debouncedCounterparty, setDebouncedCounterparty] = useState('');
  const [debouncedLocation, setDebouncedLocation] = useState('');

  // Column sorting
  const [sortColumn, setSortColumn] = useState<keyof IFlatDeal | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [filterPopoverCol, setFilterPopoverCol] = useState<string | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const filterBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleRefreshOnClick = useCallback(() => {
    setRefreshCount((c) => c + 1);
  }, []);

  const nextPageRef = useRef(1); // 1-based per SP
  const isFetchingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Debounce text inputs for server-side filtering
  useEffect(() => {
    const timer = globalThis.setTimeout(
      () => setDebouncedSearchKey(searchKey),
      NUMBERS.FIVE_HUNDREDS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [searchKey]);

  useEffect(() => {
    const timer = globalThis.setTimeout(
      () => setDebouncedShellEntity(filterShellEntity),
      NUMBERS.FIVE_HUNDREDS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [filterShellEntity]);

  useEffect(() => {
    const timer = globalThis.setTimeout(
      () => setDebouncedCounterparty(filterCounterparty),
      NUMBERS.FIVE_HUNDREDS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [filterCounterparty]);

  useEffect(() => {
    const timer = globalThis.setTimeout(
      () => setDebouncedLocation(filterLocation),
      NUMBERS.FIVE_HUNDREDS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [filterLocation]);

  // Close column filter popup when clicking outside
  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      if ((e.target as Element).closest('.col-filter-popup-fixed')) {
        return;
      }
      setFilterPopoverCol(null);
    };
    document.addEventListener('mousedown', handleDocMouseDown);
    return () => document.removeEventListener('mousedown', handleDocMouseDown);
  }, []);

  // Stable filter key — changing this resets and refetches
  const filterKey = `${periodFrom}|${periodTo}|${isViewTradesCreatedOn}|${debouncedSearchKey}|${debouncedShellEntity}|${debouncedCounterparty}|${debouncedLocation}|${filterType}|${sortColumn ?? ''}|${sortDir ?? ''}|${refreshCount}`;

  const loadMoreRows = useCallback(() => {
    if (isFetchingRef.current || !hasMoreRef.current) {
      return;
    }
    isFetchingRef.current = true;
    setIsLoading(true);
    setHasError(false);

    const payload: IFlatDealsRequest = {
      startDate: periodFrom,
      endDate: periodTo,
      viewCreatedOnDeals: isViewTradesCreatedOn,
      shellEntity: debouncedShellEntity.trim() || ALL,
      counterparty: debouncedCounterparty.trim() || ALL,
      deliveryPoint: debouncedLocation.trim() || ALL,
      dealDealLegId: debouncedSearchKey.trim() || ALL,
      type: filterType === ALL ? ALL : filterType,
      pageNumber: nextPageRef.current,
      pageSize: FLAT_DEALS_BATCH_SIZE,
      sortBy: sortColumn ?? 'importDateTime',
      sortOrder: sortDir ?? 'desc',
      dealStatus: ALL,
    };

    fetchFlatDeals(payload)
      .then((rows) => {
        setVisibleRows((prev) => [...prev, ...rows]);
        if (rows.length > 0) {
          setTotalCount(rows[0].totalCount);
        }
        nextPageRef.current += 1;
        const loadedSoFar = (nextPageRef.current - 1) * FLAT_DEALS_BATCH_SIZE;
        const knownTotal = rows[0]?.totalCount ?? 0;
        hasMoreRef.current = rows.length === FLAT_DEALS_BATCH_SIZE && loadedSoFar < knownTotal;
        setIsLoading(false);
        isFetchingRef.current = false;
      })
      .catch((err) => {
        handleError(err, COMMON_MESSAGES.ERROR);
        setHasError(true);
        setIsLoading(false);
        isFetchingRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Reset on filter change — MUST be defined before the [loadMoreRows] effect
  useEffect(() => {
    nextPageRef.current = 1;
    isFetchingRef.current = false;
    hasMoreRef.current = true;
    setVisibleRows([]);
    setTotalCount(null);
    setHasError(false);
    setIsLoading(false);
  }, [filterKey]);

  useEffect(() => {
    loadMoreRows();
  }, [loadMoreRows]);

  useEffect(() => {
    const sentinelNode = sentinelRef.current;
    if (!sentinelNode) {
      return () => {};
    }

    // Find the nearest scrolling ancestor to use as IntersectionObserver root
    let scrollParent: Element | null = null;
    let node: HTMLElement | null = sentinelNode.parentElement;
    while (node) {
      const { overflow, overflowY } = globalThis.getComputedStyle(node);
      if (/auto|scroll/.test(overflow + overflowY)) {
        scrollParent = node;
        break;
      }
      node = node.parentElement;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreRows();
        }
      },
      { root: scrollParent, rootMargin: '300px', threshold: 0 },
    );
    observer.observe(sentinelNode);
    return () => observer.disconnect();
  }, [loadMoreRows]);

  const displayRows = useMemo(() => visibleRows, [visibleRows]);

  // Source unit from API is always KWh
  const fmtQty = (value: number) => convertFromKWh(value, filterUnit);
  const handleSortCol = (col: keyof IFlatDeal) => {
    if (sortColumn !== col) {
      setSortColumn(col);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    if (sortDir === 'desc') {
      setSortDir(null);
      setSortColumn(null);
      return;
    }
    setSortDir('asc');
  };

  const applyColumnFilter = (col: FilterColumn) => {
    if (col === 'dealId') {
      setSearchKey(draftDealId.trim());
    }
    if (col === 'counterparty') {
      setFilterCounterparty(draftCounterparty.trim());
    }
    if (col === 'shellEntity') {
      setFilterShellEntity(draftShellEntity.trim());
    }
    if (col === 'deliveryPoint') {
      setFilterLocation(draftLocation.trim());
    }
    setFilterPopoverCol(null);
  };

  const clearAppliedFilter = (col: FilterColumn) => {
    if (col === 'dealId') {
      setSearchKey('');
      setDraftDealId('');
    }
    if (col === 'counterparty') {
      setFilterCounterparty('');
      setDraftCounterparty('');
    }
    if (col === 'shellEntity') {
      setFilterShellEntity('');
      setDraftShellEntity('');
    }
    if (col === 'deliveryPoint') {
      setFilterLocation('');
      setDraftLocation('');
    }
  };

  const clearAllAppliedFilters = () => {
    setSearchKey('');
    setFilterShellEntity('');
    setFilterCounterparty('');
    setFilterLocation('');
    setDraftDealId('');
    setDraftShellEntity('');
    setDraftCounterparty('');
    setDraftLocation('');
  };

  const appliedFilters = useMemo(
    () =>
      [
        { key: 'dealId', label: 'Deal Id', value: searchKey },
        { key: 'shellEntity', label: 'Shell Entity', value: filterShellEntity },
        {
          key: 'counterparty',
          label: 'Counterparty',
          value: filterCounterparty,
        },
        {
          key: 'deliveryPoint',
          label: 'Delivery Point',
          value: filterLocation,
        },
      ].filter((f) => f.value.trim()),
    [searchKey, filterShellEntity, filterCounterparty, filterLocation],
  );

  // Handler to open profile data modal and fetch profiles
  const openProfileModal = useCallback((row: IFlatDeal) => {
    setProfileModalState((prev) => ({
      ...prev,
      isOpen: true,
      dealId: row.id,
      dealLegId: row.dealDealLegId,
      isLoading: true,
    }));

    get(`${GET_FLAT_DEAL_PROFILES}?flatDealLegId=${row.id}`)
      .then((response) => {
        const profiles: IProfileData[] = response.data ?? [];
        setProfileModalState((prev) => ({
          ...prev,
          profileData: profiles,
          isLoading: false,
        }));
      })
      .catch((err) => {
        handleError(err, 'Failed to load profile data');
        setProfileModalState((prev) => ({
          ...prev,
          profileData: [],
          isLoading: false,
        }));
      });
  }, []);

  // Handler to close profile data modal
  const closeProfileModal = useCallback(() => {
    setProfileModalState({
      isOpen: false,
      profileData: [],
      isLoading: false,
    });
  }, []);

  // Handler to ignore a deal
  const handleIgnoreDeal = useCallback(
    (row: IFlatDeal) => {
      if (actionLoading[row.id]) {
        return;
      }

      setActionLoading((prev) => ({ ...prev, [row.id]: true }));

      // Controller expects [FromBody] long id — explicit JSON content-type so axios
      // serializes the raw id instead of falling back to a form-encoded body
      GenesisApi.post(SET_FLATDEAL_IGNORE, row.id, {
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => {
          handleRefreshOnClick();
          setActionLoading((prev) => ({ ...prev, [row.id]: false }));
        })
        .catch((err) => {
          handleError(err, 'Failed to ignore deal');
          setActionLoading((prev) => ({ ...prev, [row.id]: false }));
        });
    },
    [actionLoading, handleRefreshOnClick],
  );

  // Handler to reimport a deal
  const handleReimportDeal = useCallback(
    (row: IFlatDeal) => {
      if (actionLoading[row.id]) {
        return;
      }

      setActionLoading((prev) => ({ ...prev, [row.id]: true }));

      // Controller expects [FromBody] long id — explicit JSON content-type so axios
      // serializes the raw id instead of falling back to a form-encoded body
      GenesisApi.post(SET_FLATDEAL_REIMPORT, row.id, {
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => {
          handleRefreshOnClick();
          setActionLoading((prev) => ({ ...prev, [row.id]: false }));
        })
        .catch((err) => {
          handleError(err, 'Failed to reimport deal');
          setActionLoading((prev) => ({ ...prev, [row.id]: false }));
        });
    },
    [actionLoading, handleRefreshOnClick],
  );

  return (
    <>
      <FlatDealsContainer>
        <ColFilterGlobalStyle />
        {/* Summary bar — hidden for now
      <Card bodyBgColor borders={false} className='trading-balance'>
        <div className='daily-total'>
          <div className='total-trades-label'>Total Trades ({filterUnit}):</div>
          <div className='daily-counts'>
            <div className='render-capacity'>Buy {fmtQty(buyTotal)}</div>
            <div className='filter-divider'>
              <Divider direction={Directions.Vertical} />
            </div>
            <div className='render-capacity'>Sell {fmtQty(sellTotal)}</div>
            <div className='filter-divider'>
              <Divider direction={Directions.Vertical} />
            </div>
            <div className='render-capacity'>
              Net{' '}
              <Tag sentiment={net >= 0 ? 'positive' : 'negative'}>
                {net < 0 ? `-${fmtQty(Math.abs(net))}` : fmtQty(net)}
              </Tag>
            </div>
          </div>
        </div>
      </Card>
      */}

        {/* Filters card */}
        <div className='filters-card'>
          <div className='filters-row'>
            {/* Left group: date mode radio + date range */}
            <div className='filters-date-group'>
              <div className='radio-group'>
                <RadioButton
                  label='Imported on'
                  checked={isViewTradesCreatedOn}
                  value='imported'
                  onChange={() => setIsViewTradesCreatedOn(true)}
                  data-testid='flat-deals-view-imported'
                />
                <RadioButton
                  label='Active on'
                  checked={!isViewTradesCreatedOn}
                  value='active'
                  onChange={() => setIsViewTradesCreatedOn(false)}
                  data-testid='flat-deals-view-active'
                />
              </div>
              {/* Date range */}
              <div className='filter-field filter-field-date'>
                <span className='filter-label'>Date range</span>
                <RangeDatePicker
                  format='DD MMM YYYY'
                  allowClear={false}
                  value={[dayjs(periodFrom), dayjs(periodTo)]}
                  onChange={(val: RangePickerValueType) => {
                    if (!val) {
                      return;
                    }
                    const [from, to] = val;
                    if (from) {
                      setPeriodFrom(dayjs(from).format(DATE_YYYY_MM_DD));
                    }
                    if (to) {
                      setPeriodTo(dayjs(to).format(DATE_YYYY_MM_DD));
                    }
                  }}
                  size='small'
                  data-testid='flat-deals-date-range'
                />
              </div>
            </div>

            {/* Separator */}
            <div className='filters-group-sep' />

            {/* Right group: search + display unit */}
            <div className='filters-other-group'>
              {/* Display Unit */}
              <div className='filter-field'>
                <span className='filter-label'>Unit</span>
                <Select
                  size={Sizes.Small}
                  value={filterUnit}
                  className='filter-select'
                  allowClear={false}
                  showSearch={false}
                  onChange={(val) => setFilterUnit(val as string)}
                  data-testid='flat-deals-unit'
                >
                  {UNIT_OPTIONS.map((u) => (
                    <Option key={u.value} value={u.value}>
                      {u.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            {/* View toggle: Flat/Flex */}
            <div className='filters-view-toggle'>
              <ButtonGroup
                type='radio'
                size={Sizes.Small}
                selectedKey={activeView}
                items={[
                  { value: 'flat', label: 'Flat' },
                  { value: 'flex', label: 'Flex' },
                ]}
                onChange={(val) => onViewChange(val as DealView)}
                data-testid='inbox-view-toggle'
              />
            </div>

            {/* Refresh button*/}
            <div className='filters-refresh'>
              <Button
                size='small'
                iconOnly
                icon={<ArrowCw />}
                onClick={handleRefreshOnClick}
                disabled={isLoading}
                data-testid='flat-deals-refresh'
                title='Refresh'
              />
            </div>
          </div>
        </div>

        {appliedFilters.length > 0 && (
          <div className='selected-filters-bar' data-testid='flat-deals-selected-filters'>
            {appliedFilters.map((filter) => (
              <div
                key={filter.key}
                className='filter'
                data-testid={`flat-deals-applied-filter-${filter.key}`}
              >
                <div className='display-label'>{filter.label}:</div>
                <Badge size={Sizes.ExtraSmall} className='display-value'>
                  {filter.value}
                  <ShellButton
                    className='badge-button'
                    size={Sizes.ExtraSmall}
                    variant='transparent'
                    icon={<Cross />}
                    onClick={() => clearAppliedFilter(filter.key as FilterColumn)}
                    aria-label={`Clear ${filter.label} filter`}
                  />
                </Badge>
              </div>
            ))}

            <div className='filter'>
              <ShellButton
                size={Sizes.ExtraSmall}
                icon={<TrashAltSolid />}
                variant='transparent'
                onClick={clearAllAppliedFilters}
                data-testid='flat-deals-clear-all-filters'
              >
                Clear
              </ShellButton>
            </div>
          </div>
        )}

        {/* Table */}
        <div className='table-wrapper'>
          <div
            className={`table-container${isLoading ? ' table-loading' : ''}`}
            data-testid='flat-deals-scroll-area'
          >
            <Table className='shell-table' size='small'>
              <THead>
                <Tr className='table-row'>
                  {/* Import Status */}
                  <Th className='table-column' style={{ textAlign: 'center' }}>
                    Import Status
                  </Th>

                  {/* Deal Status */}
                  <Th className='table-column' style={{ textAlign: 'center' }}>
                    Deal Status
                  </Th>

                  {/* Deal Id — sort + column filter */}
                  <Th className='table-column'>
                    <div className='th-cell'>
                      <button
                        className='th-sort-trigger'
                        onClick={() => handleSortCol('dealDealLegId')}
                      >
                        Deal Id
                        <SortIcon col='dealDealLegId' sortColumn={sortColumn} sortDir={sortDir} />
                      </button>
                      <div className='th-filter-wrapper'>
                        <button
                          ref={(el) => {
                            filterBtnRefs.current['dealId'] = el;
                          }}
                          className={`th-filter-btn${filterPopoverCol === 'dealId' || searchKey ? ' active' : ''}`}
                          data-testid='flat-deals-filter-btn-dealId'
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (filterPopoverCol === 'dealId') {
                              setFilterPopoverCol(null);
                            } else {
                              setDraftDealId(searchKey);
                              const rect = filterBtnRefs.current['dealId']?.getBoundingClientRect();
                              if (rect) {
                                setPopoverPos({
                                  top: rect.bottom + 4,
                                  left: rect.left + rect.width / 2,
                                });
                              }
                              setFilterPopoverCol('dealId');
                            }
                          }}
                        >
                          <Filter width={12} height={12} />
                        </button>
                      </div>
                    </div>
                  </Th>

                  {/* Import Time */}
                  <Th
                    className='table-column th-sortable'
                    onClick={() => handleSortCol('importDateTime')}
                  >
                    <span className='th-content'>
                      Import Time
                      <SortIcon col='importDateTime' sortColumn={sortColumn} sortDir={sortDir} />
                    </span>
                  </Th>

                  {/* Period From */}
                  <Th
                    className='table-column th-sortable'
                    onClick={() => handleSortCol('deliveryBegin')}
                  >
                    <span className='th-content'>
                      Period From
                      <SortIcon col='deliveryBegin' sortColumn={sortColumn} sortDir={sortDir} />
                    </span>
                  </Th>

                  {/* Period To — sort only */}
                  <Th
                    className='table-column th-sortable'
                    onClick={() => handleSortCol('deliveryEnd')}
                  >
                    <span className='th-content'>
                      Period To
                      <SortIcon col='deliveryEnd' sortColumn={sortColumn} sortDir={sortDir} />
                    </span>
                  </Th>

                  {/* Shell Entity — sort + column filter */}
                  <Th className='table-column'>
                    <div className='th-cell'>
                      <button
                        className='th-sort-trigger'
                        onClick={() => handleSortCol('shellEntity')}
                      >
                        Shell Entity
                        <SortIcon col='shellEntity' sortColumn={sortColumn} sortDir={sortDir} />
                      </button>
                      <div className='th-filter-wrapper'>
                        <button
                          ref={(el) => {
                            filterBtnRefs.current['shellEntity'] = el;
                          }}
                          className={`th-filter-btn${filterPopoverCol === 'shellEntity' || filterShellEntity ? ' active' : ''}`}
                          data-testid='flat-deals-filter-btn-shellEntity'
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (filterPopoverCol === 'shellEntity') {
                              setFilterPopoverCol(null);
                            } else {
                              setDraftShellEntity(filterShellEntity);
                              const rect =
                                filterBtnRefs.current['shellEntity']?.getBoundingClientRect();
                              if (rect) {
                                setPopoverPos({
                                  top: rect.bottom + 4,
                                  left: rect.left + rect.width / 2,
                                });
                              }
                              setFilterPopoverCol('shellEntity');
                            }
                          }}
                        >
                          <Filter width={12} height={12} />
                        </button>
                      </div>
                    </div>
                  </Th>

                  {/* Counterparty — sort + column filter */}
                  <Th className='table-column'>
                    <div className='th-cell'>
                      <button
                        className='th-sort-trigger'
                        onClick={() => handleSortCol('counterparty')}
                      >
                        Counterparty
                        <SortIcon col='counterparty' sortColumn={sortColumn} sortDir={sortDir} />
                      </button>
                      <div className='th-filter-wrapper'>
                        <button
                          ref={(el) => {
                            filterBtnRefs.current['counterparty'] = el;
                          }}
                          className={`th-filter-btn${filterPopoverCol === 'counterparty' || filterCounterparty ? ' active' : ''}`}
                          data-testid='flat-deals-filter-btn-counterparty'
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (filterPopoverCol === 'counterparty') {
                              setFilterPopoverCol(null);
                            } else {
                              setDraftCounterparty(filterCounterparty);
                              const rect =
                                filterBtnRefs.current['counterparty']?.getBoundingClientRect();
                              if (rect) {
                                setPopoverPos({
                                  top: rect.bottom + 4,
                                  left: rect.left + rect.width / 2,
                                });
                              }
                              setFilterPopoverCol('counterparty');
                            }
                          }}
                        >
                          <Filter width={12} height={12} />
                        </button>
                      </div>
                    </div>
                  </Th>

                  {/* Delivery Point — sort + column filter */}
                  <Th className='table-column'>
                    <div className='th-cell'>
                      <button
                        className='th-sort-trigger'
                        onClick={() => handleSortCol('deliveryPoint')}
                      >
                        Delivery Point
                        <SortIcon col='deliveryPoint' sortColumn={sortColumn} sortDir={sortDir} />
                      </button>
                      <div className='th-filter-wrapper'>
                        <button
                          ref={(el) => {
                            filterBtnRefs.current['deliveryPoint'] = el;
                          }}
                          className={`th-filter-btn${filterPopoverCol === 'deliveryPoint' || filterLocation ? ' active' : ''}`}
                          data-testid='flat-deals-filter-btn-deliveryPoint'
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (filterPopoverCol === 'deliveryPoint') {
                              setFilterPopoverCol(null);
                            } else {
                              setDraftLocation(filterLocation);
                              const rect =
                                filterBtnRefs.current['deliveryPoint']?.getBoundingClientRect();
                              if (rect) {
                                setPopoverPos({
                                  top: rect.bottom + 4,
                                  left: rect.left + rect.width / 2,
                                });
                              }
                              setFilterPopoverCol('deliveryPoint');
                            }
                          }}
                        >
                          <Filter width={12} height={12} />
                        </button>
                      </div>
                    </div>
                  </Th>

                  {/* Quantity */}
                  <Th
                    className='table-column th-sortable'
                    onClick={() => handleSortCol('quantity')}
                  >
                    <span className='th-content'>
                      Quantity
                      <SortIcon col='quantity' sortColumn={sortColumn} sortDir={sortDir} />
                    </span>
                  </Th>

                  <Th className='table-column' style={{ textAlign: 'center' }}>
                    View
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {displayRows.map((row) => (
                  <Tr
                    key={row.id}
                    className={`table-row${row.importStatus === IMPORT_STATUS_IGNORED ? ' row-ignored' : ''}`}
                  >
                    {/* Import Status Cell */}
                    <Td className='table-column' style={{ textAlign: 'center' }}>
                      <ImportStatusCell
                        row={row}
                        actionLoading={actionLoading}
                        onIgnore={handleIgnoreDeal}
                        onReimport={handleReimportDeal}
                      />
                    </Td>

                    <Td className='table-column' style={{ textAlign: 'center' }}>
                      <DealStatusCell row={row} />
                    </Td>

                    <Td className='table-column'>{row.dealDealLegId}</Td>
                    <Td className='table-column'>{formatDateDisplay(row.importDateTime)}</Td>
                    <Td className='table-column'>{formatDateDisplay(row.deliveryBegin)}</Td>
                    <Td className='table-column'>{formatDateDisplay(row.deliveryEnd)}</Td>
                    <Td className='table-column'>{row.shellEntity}</Td>
                    <Td className='table-column'>{row.counterparty}</Td>
                    <Td className='table-column'>{row.deliveryPoint}</Td>

                    <Td className='table-column quantity'>{fmtQty(row.quantity)}</Td>

                    <Td className='table-column' style={{ textAlign: 'center' }}>
                      <Button
                        variant='transparent'
                        title={'View Deal Quanity'}
                        size='small'
                        key={`flat-view-btn-${row.id}`}
                        onClick={() => openProfileModal(row)}
                        data-testid={`flat-deals-profile-btn-${row.id}`}
                        icon={<EyeSolid />}
                        iconOnly
                      />
                    </Td>
                  </Tr>
                ))}
                {displayRows.length === 0 && !isLoading && (
                  <Tr className='table-row'>
                    <Td colSpan={12} className='table-column empty-cell'>
                      {hasError
                        ? 'Failed to load data. Please try again.'
                        : 'No records match the selected filters'}
                    </Td>
                  </Tr>
                )}
              </TBody>
            </Table>

            <div ref={sentinelRef} className='load-sentinel' />
          </div>

          {isLoading && (
            <div className='loading-more' data-testid='flat-deals-loading'>
              <Loader />
            </div>
          )}
        </div>

        {/* Row count footer */}
        {visibleRows.length > 0 && (
          <div className='row-count'>
            <span className='row-count-loaded'>
              {displayRows.length.toLocaleString('en-GB')} loaded
            </span>
            {totalCount !== null && (
              <>
                <span className='row-count-sep'>·</span>
                <span className='row-count-total'>
                  {totalCount.toLocaleString('en-GB')} Total Records
                </span>
              </>
            )}
          </div>
        )}
      </FlatDealsContainer>

      {/* Column filter popups rendered via portal so they escape overflow:auto clipping */}
      {filterPopoverCol === 'dealId' &&
        createPortal(
          <div
            className='col-filter-popup-fixed'
            role='tooltip'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-fixed'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by deal id'
                value={draftDealId}
                onChange={(e) => setDraftDealId(e.target.value)}
                data-testid='col-filter-deal-id'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('dealId')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'deliveryPoint' &&
        createPortal(
          <div
            className='col-filter-popup-fixed'
            role='tooltip'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-fixed'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by delivery point'
                value={draftLocation}
                onChange={(e) => setDraftLocation(e.target.value)}
                data-testid='col-filter-location'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('deliveryPoint')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'counterparty' &&
        createPortal(
          <div
            className='col-filter-popup-fixed col-filter-popup-fixed-wide'
            role='tooltip'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-fixed'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by counterparty'
                value={draftCounterparty}
                onChange={(e) => setDraftCounterparty(e.target.value)}
                data-testid='col-filter-counterparty'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('counterparty')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}
      {filterPopoverCol === 'shellEntity' &&
        createPortal(
          <div
            className='col-filter-popup-fixed col-filter-popup-fixed-wide'
            role='tooltip'
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <div className='col-filter-input-row-fixed'>
              <TextInput
                size={Sizes.Small}
                placeholder='Filter by shell entity'
                value={draftShellEntity}
                onChange={(e) => setDraftShellEntity(e.target.value)}
                data-testid='col-filter-shell-entity'
                autoFocus
              />
              <Button size='small' onClick={() => applyColumnFilter('shellEntity')}>
                Apply
              </Button>
            </div>
          </div>,
          document.body,
        )}

      {/* Profile Data Modal */}
      <ProfileDataModal
        isOpen={profileModalState.isOpen}
        dealLegId={profileModalState.dealLegId}
        profileData={profileModalState.profileData}
        isLoading={profileModalState.isLoading}
        onClose={closeProfileModal}
      />
    </>
  );
};

export default FlatDeals;
